import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalHeader } from "components/modal";
import ButtonNew from "components/buttonNew/buttonNew";
import { Close } from "assets/icons";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectItem,
  Card,
  CardBody,
  Divider,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@heroui/react";
import { API_URL } from "constants/index";
import { LoadingOverlay } from "components/LoadingSpinner";
import { ArrowsMerge, Warning } from "@phosphor-icons/react";

interface MergeCompanyModalProps {
  onClose?: () => void;
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  companies: any[];
  refreshParentCompanies: () => void;
}

type Step = "select" | "preview";

interface MergePreviewResponse {
  sourceCompany: {
    id: number;
    name: string;
    odooId: number;
  };
  targetCompany: {
    id: number;
    name: string;
    odooId: number;
  };
  impacts: {
    buildingsCount: number;
    buildingIds: string[];
    contractsCount: number;
    contractIds: number[];
    productsCount: number;
    productMappings: Array<{
      sourceProductId: number;
      sourceProductName: string;
      targetProductId: number | null;
      targetProductName: string | null;
      willBeCreated: boolean;
    }>;
  };
  validationErrors: string[];
  canProceed: boolean;
}

const MergeCompanyModal: React.FC<MergeCompanyModalProps> = ({
  onClose,
  isOpen,
  onOpenChange,
  companies,
}) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("select");
  const [sourceCompanyId, setSourceCompanyId] = useState<number | null>(null);
  const [targetCompanyId, setTargetCompanyId] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<MergePreviewResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Reset state when modal opens
      setStep("select");
      setSourceCompanyId(null);
      setTargetCompanyId(null);
      setPreviewData(null);
      setIsLoading(false);
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleClose = () => {
    if (isLoading) return;
    onClose?.();
    onOpenChange?.(false);
  };

  const fetchPreview = async () => {
    if (!sourceCompanyId || !targetCompanyId) {
      return toast.error("Selecteer beide bedrijven");
    }

    setIsLoading(true);
    setLoadingMessage("Fusie-impact wordt berekend...");

    try {
      const response = await fetchWithAuth(
        `${API_URL}/companies/merge/preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sourceCompanyId, targetCompanyId }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Preview ophalen mislukt");
      }

      const data: MergePreviewResponse = await response.json();
      setPreviewData(data);

      if (data.canProceed) {
        setStep("preview");
      } else {
        toast.error(
          "Fusie kan niet worden uitgevoerd: " +
            data.validationErrors.join(", "),
        );
      }
    } catch (error: any) {
      toast.error("Fout bij ophalen preview: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const executeMerge = async () => {
    if (!sourceCompanyId || !targetCompanyId) return;

    setIsLoading(true);
    setLoadingMessage("Fusie wordt gestart...");

    try {
      const response = await fetchWithAuth(
        `${API_URL}/companies/merge/execute-async`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sourceCompanyId, targetCompanyId }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Fusie starten mislukt");
      }

      const result = await response.json();
      const jobId = result.jobId;

      // Close modal and navigate to progress page
      handleClose();
      navigate(`/vennootschappen/merge/${jobId}`);
      toast.success("Fusie gestart! Volg de voortgang op de nieuwe pagina.");
    } catch (error: any) {
      toast.error("Fout bij starten fusie: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSelectionStep = () => (
    <>
      <Card className="mb-4">
        <CardBody>
          <div className="mb-4 flex items-center gap-2">
            <ArrowsMerge size={24} />
            <h3 className="text-lg font-semibold">Selecteer bedrijven</h3>
          </div>
          <p className="mb-4 text-sm text-gray-600">
            Kies het bronbedrijf (wordt gefuseerd) en het doelbedrijf (blijft
            bestaan).
          </p>

          <div className="space-y-4">
            <Select
              label="Bronbedrijf (wordt gefuseerd)"
              placeholder="Selecteer bronbedrijf"
              value={sourceCompanyId?.toString() || ""}
              onChange={(e) =>
                setSourceCompanyId(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              isDisabled={isLoading}
              variant="bordered"
              classNames={{
                label: "text-sm font-medium text-neutral-primary",
                value: "text-sm text-neutral-primary font-normal",
                trigger: "data-[hover=true]:bg-gray-100",
              }}
            >
              {companies
                .filter((c) => !c.archived && !c.mergedWithCompanyId)
                .map((company) => (
                  <SelectItem
                    key={company.id.toString()}
                    textValue={`${company.name} (ID: ${company.id})`}
                  >
                    {company.name} (ID: {company.id})
                  </SelectItem>
                ))}
            </Select>

            <Select
              label="Doelbedrijf (blijft bestaan)"
              placeholder="Selecteer doelbedrijf"
              value={targetCompanyId?.toString() || ""}
              onChange={(e) =>
                setTargetCompanyId(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              isDisabled={isLoading}
              variant="bordered"
              classNames={{
                label: "text-sm font-medium text-neutral-primary",
                value: "text-sm text-neutral-primary font-normal",
                trigger: "data-[hover=true]:bg-gray-100",
              }}
            >
              {companies
                .filter(
                  (c) =>
                    !c.archived &&
                    !c.mergedWithCompanyId &&
                    c.id !== sourceCompanyId,
                )
                .map((company) => (
                  <SelectItem
                    key={company.id.toString()}
                    textValue={`${company.name} (ID: ${company.id})`}
                  >
                    {company.name} (ID: {company.id})
                  </SelectItem>
                ))}
            </Select>
          </div>
        </CardBody>
      </Card>

      <div className="flex justify-end gap-2">
        <ButtonNew
          onClick={handleClose}
          isDisabled={isLoading}
          label="Annuleren"
        />
        <ButtonNew
          variant="primaryBrick"
          onClick={fetchPreview}
          isDisabled={!sourceCompanyId || !targetCompanyId || isLoading}
          label="Bekijk impact"
        />
      </div>
    </>
  );

  const renderPreviewStep = () => {
    if (!previewData) return null;

    return (
      <>
        <Card className="mb-4">
          <CardBody>
            <div className="mb-4 flex items-center gap-2">
              <Warning size={24} className="text-yellow-500" />
              <h3 className="text-lg font-semibold">Impact van fusie</h3>
            </div>

            <div className="space-y-4">
              <p className="text-sm">
                <strong>Contracten in de boekhouding</strong> gaan van{" "}
                <span className="font-bold">
                  {previewData.sourceCompany.name}
                </span>{" "}
                naar{" "}
                <span className="font-bold">
                  {previewData.targetCompany.name}
                </span>{" "}
                waardoor facturatie en boekingen op de eerste van volgende maand
                geboekt worden op {previewData.targetCompany.name}.
              </p>

              <p className="text-sm">
                <strong>Producten in de contracten</strong> van{" "}
                {previewData.sourceCompany.name} worden gewijzigd naar de
                producten van {previewData.targetCompany.name}.
              </p>

              <p className="text-sm">
                Alle <strong>gebouwen die eigendom zijn</strong> van{" "}
                {previewData.sourceCompany.name} worden eigendom van{" "}
                {previewData.targetCompany.name}.
              </p>

              <Divider />

              <div>
                <h4 className="mb-2 font-semibold">Aantallen:</h4>
                <ul className="list-inside list-disc space-y-1 text-sm">
                  <li>Gebouwen: {previewData.impacts.buildingsCount}</li>
                  <li>Contracten: {previewData.impacts.contractsCount}</li>
                  <li>Producten: {previewData.impacts.productsCount}</li>
                </ul>
              </div>

              {previewData.impacts.productMappings.length > 0 && (
                <>
                  <Divider />
                  <div>
                    <h4 className="mb-2 font-semibold">Product mappings:</h4>
                    <Table
                      aria-label="Product mappings"
                      className="max-h-60 overflow-auto"
                    >
                      <TableHeader>
                        <TableColumn>Bronproduct</TableColumn>
                        <TableColumn>Doelproduct</TableColumn>
                        <TableColumn>Actie</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {previewData.impacts.productMappings.map((mapping) => (
                          <TableRow key={mapping.sourceProductId}>
                            <TableCell>{mapping.sourceProductName}</TableCell>
                            <TableCell>
                              {mapping.targetProductName || "-"}
                            </TableCell>
                            <TableCell>
                              {mapping.willBeCreated ? (
                                <span className="text-blue-600">
                                  Wordt aangemaakt
                                </span>
                              ) : (
                                <span className="text-green-600">
                                  Bestaat al
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          </CardBody>
        </Card>

        <div className="flex justify-end gap-2">
          <ButtonNew
            variant="tertiary"
            onClick={() => setStep("select")}
            isDisabled={isLoading}
            label="Terug"
          />
          <ButtonNew
            variant="primaryBrick"
            onClick={executeMerge}
            isDisabled={isLoading}
            label="Fusie uitvoeren"
          />
        </div>
      </>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span>Bedrijven fuseren</span>
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <Close />
              </button>
            </div>
          </ModalHeader>
          <ModalBody>
            {step === "select" && renderSelectionStep()}
            {step === "preview" && renderPreviewStep()}
          </ModalBody>
        </ModalContent>
      </Modal>

      {isLoading && <LoadingOverlay message={loadingMessage} />}
    </>
  );
};

export default MergeCompanyModal;
